// TOTP (RFC 6238) sobre HOTP (RFC 4226), implementado con Web Crypto —
// sin dependencias externas. Usado para la verificación en dos pasos
// app-level en Ajustes de Perfil.

const BASE32_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";

export const base32Encode = (bytes) => {
  let bits = "";
  for (const byte of bytes) {
    bits += byte.toString(2).padStart(8, "0");
  }
  let output = "";
  for (let i = 0; i < bits.length; i += 5) {
    const chunk = bits.slice(i, i + 5).padEnd(5, "0");
    output += BASE32_ALPHABET[parseInt(chunk, 2)];
  }
  return output;
};

const base32Decode = (input) => {
  const clean = input.toUpperCase().replace(/=+$/, "").replace(/\s+/g, "");
  let bits = "";
  for (const char of clean) {
    const val = BASE32_ALPHABET.indexOf(char);
    if (val === -1) continue;
    bits += val.toString(2).padStart(5, "0");
  }
  const bytes = [];
  for (let i = 0; i + 8 <= bits.length; i += 8) {
    bytes.push(parseInt(bits.slice(i, i + 8), 2));
  }
  return new Uint8Array(bytes);
};

export const generateSecret = (byteLength = 20) => {
  const bytes = new Uint8Array(byteLength);
  window.crypto.getRandomValues(bytes);
  return base32Encode(bytes);
};

const BACKUP_CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // sin 0/O/1/I
export const generateBackupCodes = (count = 10) => {
  const codes = [];
  for (let i = 0; i < count; i += 1) {
    const bytes = new Uint8Array(8);
    window.crypto.getRandomValues(bytes);
    let code = "";
    bytes.forEach((b) => {
      code += BACKUP_CODE_ALPHABET[b % BACKUP_CODE_ALPHABET.length];
    });
    codes.push(`${code.slice(0, 4)}-${code.slice(4, 8)}`);
  }
  return codes;
};

export const sha256Hex = async (text) => {
  const data = new TextEncoder().encode(text);
  const hashBuffer = await window.crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

const hotp = async (secretBase32, counter, digits = 6) => {
  const keyBytes = base32Decode(secretBase32);
  const counterBytes = new ArrayBuffer(8);
  const counterView = new DataView(counterBytes);
  // JS no tiene enteros de 64 bits nativos; el contador de TOTP (steps de 30s)
  // no llega a superar los 32 bits en ningún futuro cercano.
  counterView.setUint32(4, counter, false);

  const cryptoKey = await window.crypto.subtle.importKey(
    "raw",
    keyBytes,
    { name: "HMAC", hash: "SHA-1" },
    false,
    ["sign"]
  );
  const signature = new Uint8Array(
    await window.crypto.subtle.sign("HMAC", cryptoKey, counterBytes)
  );

  const offset = signature[signature.length - 1] & 0x0f;
  const binCode =
    ((signature[offset] & 0x7f) << 24) |
    ((signature[offset + 1] & 0xff) << 16) |
    ((signature[offset + 2] & 0xff) << 8) |
    (signature[offset + 3] & 0xff);

  return String(binCode % 10 ** digits).padStart(digits, "0");
};

export const totp = async (secretBase32, { step = 30, digits = 6, timestamp = Date.now() } = {}) => {
  const counter = Math.floor(timestamp / 1000 / step);
  return hotp(secretBase32, counter, digits);
};

// Acepta el código actual y el de la ventana anterior/siguiente (drift de reloj).
export const verifyTotp = async (secretBase32, token, { step = 30, digits = 6, window: driftWindow = 1 } = {}) => {
  const cleanToken = String(token || "").trim();
  if (!/^\d{6,8}$/.test(cleanToken)) return false;

  const now = Date.now();
  for (let errorWindow = -driftWindow; errorWindow <= driftWindow; errorWindow += 1) {
    const candidate = await totp(secretBase32, { step, digits, timestamp: now + errorWindow * step * 1000 });
    if (candidate === cleanToken) return true;
  }
  return false;
};

export const buildOtpAuthUri = (secretBase32, accountLabel, issuer = "Notiline") =>
  `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(accountLabel)}?secret=${secretBase32}&issuer=${encodeURIComponent(issuer)}&algorithm=SHA1&digits=6&period=30`;
